<template>
  <div>
    <div
      v-show="$store.state.upload.isPopupUpload"
      id="card-file-uploader"
      class="card card-uploader position-fixed"
      :style="widgetStyle"
    >
      <div
        class="card-header header bg-primary d-flex justify-content-between align-items-center"
        @pointerdown="startDrag"
      >
        <div class="d-flex align-items-center">
          <span class="text-white font-weight-bold me-3">
            {{ $t('global.fileUploader.title') }}
          </span>
          <span
            v-if="hasActiveUploads"
            class="ml-2 spinner-border spinner-border-sm text-white"
          ></span>
        </div>
        <div class="d-flex align-items-center" @pointerdown.stop>
          <div class="font-size-20 mr-2 text-white cursor-pointer mt-1" @click="toggleList">
            <i :class="`mc-icon icon-outline_chevron-${expandList ? 'up' : 'down'}`"></i>
          </div>
          <div
            v-if="!hasActiveUploads"
            class="font-size-20 text-white cursor-pointer"
            @click="closePopup"
          >
            &times;
          </div>
        </div>
      </div>

      <div v-if="expandList" class="card-body body">
        <div v-if="uploads.length === 0" class="text-center py-4">
          {{ $t('global.fileUploader.noUploads') }}
        </div>
        <div v-else class="upload-list-container">
          <UploadItem
            v-for="upload in uploads"
            :key="upload.id"
            :upload="upload"
            @resume="requestResume"
            @pause="pauseUpload"
            @cancel="showCancelConfirmModal"
            @remove="removeUpload"
          />
        </div>
      </div>
    </div>

    <input
      ref="resumeFileInput"
      type="file"
      :accept="resumeAccept"
      class="d-none"
      @change="handleResumeFile"
    />

    <b-modal
      id="cancel-upload-modal"
      :title="$t('global.fileUploader.cancelModal.title')"
      centered
      :ok-title="$t('global.fileUploader.cancelModal.confirm')"
      :cancel-title="$t('global.fileUploader.cancelModal.dismiss')"
      ok-variant="primary"
      @ok="confirmCancelUpload"
      @cancel="hideCancelConfirmModal"
    >
      <p class="mb-0">
        {{ $t('global.fileUploader.cancelModal.question') }}
      </p>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import UploadItem from './UploadItem.vue'
import draggableWidget from './draggable-widget'
import { getUploadHandler } from '@/services/upload/registry'
import { supportsPersistentFileHandle } from '@/services/upload/file-handle-store'
import {
  isUploadOwnedByAnotherTab,
  isUploadOwnedByCurrentTab,
  withUploadOwnership,
} from '@/services/upload/tab-coordinator'
import {
  isUploadPageUnloading,
  markUploadPageUnloading,
  resetUploadPageLifecycle,
} from '@/services/upload/page-lifecycle'

const ACTIONABLE_STATUSES = ['uploading', 'processing', 'paused', 'waiting_resume', 'canceling']
const UPLOAD_STORAGE_KEY = 'mcFileUploads'
const UPLOAD_INTERRUPTED_KEY = 'global.fileUploader.messages.uploadInterrupted'
const MAX_AUTO_RESUME_ATTEMPTS = 3

export default {
  name: 'McFileUploader',
  components: { UploadItem },
  mixins: [draggableWidget],
  data() {
    return {
      expandList: true,
      currentCancelUpload: null,
      currentResumeUpload: null,
      resumeAccept: '',
      ownershipRecoveryTimer: null,
    }
  },
  computed: {
    ...mapGetters('upload', ['activeUploads']),
    uploads() {
      return this.$store.state.upload.uploads
    },
    hasActiveUploads() {
      return this.activeUploads.length > 0
    },
    widgetStyle() {
      const style = {
        zIndex: 9999,
        width: 'min(400px, calc(100vw - 24px))',
      }

      if (this.widgetPosition) {
        return {
          ...style,
          left: `${this.widgetPosition.left}px`,
          top: `${this.widgetPosition.top}px`,
        }
      }

      return { ...style, right: '20px', bottom: '20px' }
    },
  },
  async mounted() {
    resetUploadPageLifecycle()
    window.addEventListener('storage', this.handleUploadStorageChange)
    window.addEventListener('beforeunload', this.markPageUnloading)
    window.addEventListener('pagehide', this.markPageUnloading)
    await this.$store.dispatch('upload/hydrateUploads')
    this.restoreResumableUploads()
    this.ownershipRecoveryTimer = window.setInterval(this.recoverStaleUploads, 3000)
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.handleUploadStorageChange)
    window.removeEventListener('beforeunload', this.markPageUnloading)
    window.removeEventListener('pagehide', this.markPageUnloading)
    window.clearInterval(this.ownershipRecoveryTimer)
  },
  methods: {
    markPageUnloading() {
      markUploadPageUnloading()
    },
    handleUploadStorageChange(event) {
      if (event.key !== UPLOAD_STORAGE_KEY || !event.newValue) return

      let externalUploads
      try {
        externalUploads = JSON.parse(event.newValue)
      } catch {
        return
      }
      if (!Array.isArray(externalUploads)) return

      const externalStatuses = new Map(
        externalUploads.map((upload) => [upload.id, upload.status])
      )
      let hasSynchronizedCancellation = false

      this.uploads.forEach((upload) => {
        if (
          externalStatuses.get(upload.id) !== 'canceled' ||
          !ACTIONABLE_STATUSES.includes(upload.status)
        ) {
          return
        }

        const handler = getUploadHandler(upload.uploadType, upload)
        if (handler?.pause) handler.pause({ upload })
        this.$store.commit('upload/cancelUpload', upload.id)
        hasSynchronizedCancellation = true
      })

      externalUploads.forEach((externalUpload) => {
        const localUpload = this.uploads.find((upload) => upload.id === externalUpload.id)
        if (!localUpload) {
          this.$store.commit('upload/addUpload', externalUpload)
          return
        }

        if (
          externalUpload.status !== 'canceled' &&
          !isUploadOwnedByCurrentTab(localUpload)
        ) {
          this.$store.commit('upload/updateUpload', externalUpload)
        }
      })

      if (externalUploads.some((upload) => ACTIONABLE_STATUSES.includes(upload.status))) {
        this.$store.commit('upload/showUploadPopup', true)
      }

      if (hasSynchronizedCancellation) this.persistUploads()

      if (
        hasSynchronizedCancellation &&
        !this.uploads.some((upload) => ACTIONABLE_STATUSES.includes(upload.status))
      ) {
        this.$store.commit('upload/showUploadPopup', false)
      }
    },
    persistUploads() {
      this.$store.dispatch('upload/persistUploads')
    },
    updateUpload(id, updates) {
      this.$store.commit('upload/updateUpload', { id, ...updates })
      this.persistUploads()
    },
    toggleList() {
      this.expandList = !this.expandList
    },
    closePopup() {
      if (!this.hasActiveUploads) this.$store.commit('upload/showUploadPopup', false)
    },
    async removeUpload(uploadId) {
      const upload = this.uploads.find((item) => item.id === uploadId)
      const handler = getUploadHandler(upload?.uploadType, upload)
      if (handler?.cleanup) await handler.cleanup(upload)
      this.$store.commit('upload/removeUpload', uploadId)
      this.persistUploads()
    },
    async restoreResumableUploads() {
      const resumableUploads = this.uploads.filter(
        (upload) =>
          upload.status === 'waiting_resume' &&
          (upload.interruptionReason !== 'transient' ||
            Number(upload.nextAutoResumeAt || 0) <= Date.now())
      )
      for (const upload of resumableUploads) {
        const handler = getUploadHandler(upload.uploadType, upload)
        if (!handler?.restoreFile) continue

        const file = await handler.restoreFile({ upload }).catch(() => null)
        if (file) await this.resumeUpload(upload, file, handler)
      }
    },
    async recoverStaleUploads() {
      const staleUploads = this.uploads.filter(
        (upload) =>
          ['uploading', 'processing'].includes(upload.status) &&
          !isUploadOwnedByCurrentTab(upload) &&
          !isUploadOwnedByAnotherTab(upload)
      )

      for (const upload of staleUploads) {
        this.updateUpload(upload.id, {
          status: 'waiting_resume',
          error: null,
          errorKey: null,
          interruptionReason: 'page-refresh',
        })
      }

      const recoverableUploads = this.uploads.filter(
        (upload) =>
          upload.status === 'waiting_resume' &&
          upload.interruptionReason === 'transient' &&
          Number(upload.nextAutoResumeAt || 0) <= Date.now() &&
          Number(upload.autoResumeAttempts || 0) <= MAX_AUTO_RESUME_ATTEMPTS
      )

      if (staleUploads.length || recoverableUploads.length) {
        await this.restoreResumableUploads()
      }
    },
    pauseUpload(upload) {
      const handler = getUploadHandler(upload.uploadType, upload)
      if (!handler?.pause) return

      this.updateUpload(upload.id, {
        status: 'paused',
        error: null,
        errorKey: null,
        interruptionReason: 'manual',
      })
      handler.pause({ upload })
    },
    async requestResume(upload) {
      const handler = getUploadHandler(upload.uploadType, upload)
      if (!handler?.resume) {
        this.$mcToast.error(this.$t('global.fileUploader.messages.cannotResume'))
        return
      }

      if (upload.file) {
        await this.resumeUpload(upload, upload.file, handler)
        return
      }

      if (handler.restoreFile) {
        const restoredFile = await handler
          .restoreFile({ upload, requestPermission: true })
          .catch(() => null)
        if (restoredFile) {
          await this.resumeUpload(upload, restoredFile, handler)
          return
        }
      }

      this.currentResumeUpload = upload
      this.updateUpload(upload.id, {
        requiresFileReselection: !supportsPersistentFileHandle(),
      })
      this.resumeAccept = handler.accept || ''
      this.$refs.resumeFileInput.value = ''
      this.$nextTick(() => this.$refs.resumeFileInput.click())
    },
    handleResumeFile(event) {
      const file = event.target.files[0]
      const upload = this.currentResumeUpload
      if (!file || !upload) return

      const handler = getUploadHandler(upload.uploadType, upload)
      const isSameFile = handler?.validateResumeFile
        ? handler.validateResumeFile(file, upload)
        : file.name === upload.fileName && file.size === upload.fileSize

      if (!isSameFile) {
        this.$mcToast.error(this.$t('global.fileUploader.messages.differentFile'))
        this.currentResumeUpload = null
        return
      }

      this.currentResumeUpload = null
      this.updateUpload(upload.id, { requiresFileReselection: false })
      this.resumeUpload(upload, file, handler)
    },
    async resumeUpload(upload, file, handler) {
      const uploadType = upload.uploadType || handler.type
      await withUploadOwnership({
        uploadId: upload.id,
        onOwnershipChange: (updates) => this.updateUpload(upload.id, updates),
        task: async () => {
          this.updateUpload(upload.id, {
            uploadType,
            status: 'uploading',
            error: null,
            errorKey: null,
            interruptionReason: null,
            requiresFileReselection: false,
            file,
          })

          try {
            const result = await handler.resume({
              upload,
              file,
              apollo: this.$apollo,
              update: (updates) =>
                this.updateUpload(upload.id, {
                  ...updates,
                  ...(updates.progress !== undefined
                    ? { autoResumeAttempts: 0, nextAutoResumeAt: null }
                    : {}),
                }),
            })

            this.$store.commit('upload/completeUpload', upload.id)
            this.persistUploads()
            this.$root.$emit('file-upload-complete', { ...upload, uploadType, result })
            this.$mcToast.success(this.$t('global.fileUploader.messages.uploadSuccess'))
          } catch (error) {
            if (error.name === 'AbortError' || isUploadPageUnloading()) return
            await this.handleUploadFailure(upload, error)
          }
        },
      })
    },
    async handleUploadFailure(upload, error) {
      if (!error.isFatalUploadError && !error.isUserError) {
        const currentUpload = this.uploads.find((item) => item.id === upload.id)
        const autoResumeAttempts = Number(currentUpload?.autoResumeAttempts || 0) + 1
        this.updateUpload(upload.id, {
          status:
            autoResumeAttempts <= MAX_AUTO_RESUME_ATTEMPTS ? 'waiting_resume' : 'paused',
          error: null,
          errorKey: null,
          interruptionReason: 'transient',
          autoResumeAttempts,
          nextAutoResumeAt: Date.now() + 3000 * autoResumeAttempts,
        })
        return
      }

      this.updateUpload(upload.id, {
        status: 'paused',
        error: null,
        errorKey: UPLOAD_INTERRUPTED_KEY,
        interruptionReason: 'runtime-error',
      })
      this.$mcToast.error(this.$t(UPLOAD_INTERRUPTED_KEY))
    },
    showCancelConfirmModal(upload) {
      this.currentCancelUpload = upload
      this.$bvModal.show('cancel-upload-modal')
    },
    hideCancelConfirmModal() {
      this.currentCancelUpload = null
      this.$bvModal.hide('cancel-upload-modal')
    },
    async confirmCancelUpload() {
      if (!this.currentCancelUpload) return

      const upload = this.currentCancelUpload
      const handler = getUploadHandler(upload.uploadType, upload)
      this.updateUpload(upload.id, { status: 'canceling' })

      try {
        if (handler?.cancel) await handler.cancel({ upload, apollo: this.$apollo })
        this.$store.commit('upload/cancelUpload', upload.id)
        this.$root.$emit('file-upload-canceled', { ...upload, status: 'canceled' })
      } catch (error) {
        const errorKey = 'global.fileUploader.messages.cancelFailed'
        this.$store.commit('upload/failUpload', { id: upload.id, error: null, errorKey })
        this.$mcToast.error(this.$t(errorKey))
      }

      this.persistUploads()
      this.hideCancelConfirmModal()
    },
  },
}
</script>

<style lang="scss" scoped>
.card-uploader {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow: hidden;
}

.header {
  padding: 12px 16px;
  cursor: move;
  user-select: none;
  touch-action: none;
}

.body {
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}

</style>
